import * as RadixTooltip from '@radix-ui/react-tooltip';
import classNames from 'classnames';
import {
  createContext,
  useContext,
  useState,
  type FC,
  type FocusEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { useWindowDocument } from '../../../context/WindowDocumentContext';
import styles from './Tooltip.module.scss';

interface ITooltipProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  content?: ReactNode;
  type?: 'dark' | 'light';
  helperText?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  delayDuration?: number;
  maxWidth?: number;
  canOpen?: () => boolean;
  disabled?: boolean;
}

interface ITooltipGroupProps {
  children: ReactNode;
  delayDuration?: number;
}

const TooltipGroupContext = createContext(false);

/** Shares one delay across the tooltips inside it, so once one is open its neighbours open instantly. */
export const TooltipGroup: FC<ITooltipGroupProps> = ({ children, delayDuration = 300 }) => {
  'use memo';

  return (
    <RadixTooltip.Provider delayDuration={delayDuration} disableHoverableContent>
      <TooltipGroupContext.Provider value>{children}</TooltipGroupContext.Provider>
    </RadixTooltip.Provider>
  );
};

export const Tooltip: FC<ITooltipProps> = ({
  children,
  title,
  subtitle,
  content,
  type = 'dark',
  helperText,
  side = 'top',
  align = 'center',
  sideOffset = 6,
  delayDuration = 300,
  maxWidth,
  canOpen,
  disabled,
}) => {
  'use memo';
  const windowDoc = useWindowDocument();
  const isGrouped = useContext(TooltipGroupContext);
  const [isOpen, setIsOpen] = useState(false);
  // An empty tooltip stays mounted but never opens, so callers can switch it off without remounting the trigger
  const hasContent = !!(title || subtitle || content);

  if (disabled && isOpen) setIsOpen(false);

  const canShow = () => hasContent && !disabled && (canOpen?.() ?? true);

  const handleTriggerPointerMove = (event: PointerEvent<HTMLElement>) => {
    if (!canShow()) event.preventDefault();
  };

  const handleTriggerFocus = (event: FocusEvent<HTMLElement>) => {
    if (!event.target.matches(':focus-visible') || !canShow()) event.preventDefault();
  };

  const tooltip = (
    <RadixTooltip.Root open={hasContent && isOpen} onOpenChange={open => setIsOpen(open && canShow())}>
      <RadixTooltip.Trigger asChild onPointerMove={handleTriggerPointerMove} onFocus={handleTriggerFocus}>
        {children}
      </RadixTooltip.Trigger>
      <RadixTooltip.Portal container={windowDoc.body}>
        <RadixTooltip.Content
          className={classNames(styles.content, {
            [styles.contentDark]: type === 'dark',
            [styles.contentLight]: type === 'light',
            [styles.contentWithHelperText]: !!helperText,
          })}
          data-theme={type === 'dark' ? 'dark' : undefined}
          side={side}
          align={align}
          sideOffset={sideOffset}
          style={maxWidth ? { maxWidth } : undefined}
        >
          {content ? (
            content
          ) : (
            <div className={styles.text}>
              <span className={styles.title}>{title}</span>
              {subtitle && !helperText && <span className={styles.subtitle}>{subtitle}</span>}
            </div>
          )}
          {helperText && <span className={styles.helperText}>{helperText}</span>}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );

  return isGrouped ? (
    tooltip
  ) : (
    <RadixTooltip.Provider delayDuration={delayDuration} disableHoverableContent>
      {tooltip}
    </RadixTooltip.Provider>
  );
};
